import React, { useState } from "react";
import Icon from "../../../components/AppIcon";
import Input from "../../../components/ui/Input";
import Button from "../../../components/ui/Button";

const ManualEntryPanel = ({ onSubmit }) => {
  const [cashTips, setCashTips] = useState("");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [notes, setNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};

    if (!cashTips || parseFloat(cashTips) <= 0) {
      newErrors.cashTips = "Please enter a valid amount greater than 0";
    }

    if (parseFloat(cashTips) > 10000) {
      newErrors.cashTips = "Amount seems unusually high. Please verify.";
    }

    if (!date) {
      newErrors.date = "Please select a date";
    }

    // Don't allow future dates
    const selectedDate = new Date(date);
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    if (selectedDate > today) {
      newErrors.date = "Cannot enter tips for future dates";
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);
    await onSubmit({
      cashTips: parseFloat(cashTips),
      date: date,
      notes,
      timestamp: new Date()?.toISOString(),
      isManualEntry: true,
    });

    setTimeout(() => {
      setCashTips("");
      setNotes("");
      // Keep date as-is for convenience if adding multiple entries for the same day
      setIsSubmitting(false);
    }, 1000);
  };

  const formatCurrency = (value) => {
    if (!value) return "£0.00";
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })?.format(value);
  };

  const formatSelectedDate = () => {
    if (!date) return "";
    const d = new Date(date);
    return d.toLocaleDateString("en-GB", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  return (
    <div className="bg-card rounded-xl border border-border p-4 md:p-6">
      <div className="flex items-center gap-3 mb-4 md:mb-6">
        <div
          className="w-10 h-10 md:w-12 md:h-12 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: "rgba(74, 103, 65, 0.1)" }}
        >
          <Icon
            name="PenLine"
            size={20}
            color="var(--color-secondary)"
            className="md:w-6 md:h-6"
          />
        </div>
        <div>
          <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground">
            Manual Cash Tips Entry
          </h3>
          <p className="text-xs md:text-sm font-caption text-muted-foreground">
            Record cash tips not captured by till system
          </p>
        </div>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Date Field */}
        <div>
          <label className="block text-sm font-caption font-semibold text-foreground mb-2">
            Date <span className="text-error">*</span>
          </label>
          <Input
            type="date"
            value={date}
            onChange={(e) => setDate(e?.target?.value)}
            error={errors?.date}
            max={new Date().toISOString().split("T")[0]}
            required
          />
          {date && !errors?.date && (
            <p className="text-xs font-caption text-muted-foreground mt-1">
              {formatSelectedDate()}
            </p>
          )}
        </div>

        {/* Amount Field */}
        <Input
          type="number"
          label="Cash Tips Amount"
          description="Enter total cash tips received"
          placeholder="0.00"
          value={cashTips}
          onChange={(e) => setCashTips(e?.target?.value)}
          error={errors?.cashTips}
          required
          min="0.01"
          step="0.01"
        />

        {cashTips && parseFloat(cashTips) > 0 && (
          <div className="bg-muted rounded-lg p-3 md:p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-caption text-muted-foreground">
                Amount to add:
              </span>
              <span className="text-lg md:text-xl font-data font-semibold text-primary">
                {formatCurrency(parseFloat(cashTips))}
              </span>
            </div>
            {date && (
              <div className="flex items-center justify-between mt-2 pt-2 border-t border-border">
                <span className="text-sm font-caption text-muted-foreground">
                  For date:
                </span>
                <span className="text-sm font-medium">
                  {new Date(date).toLocaleDateString("en-GB")}
                </span>
              </div>
            )}
          </div>
        )}

        <div>
          <label className="block text-sm font-caption font-semibold text-foreground mb-2">
            Notes (Optional)
          </label>
          <textarea
            className="w-full px-4 py-3 rounded-lg border border-border bg-input text-foreground font-caption text-sm focus:outline-none focus:ring-2 focus:ring-ring transition-base resize-none"
            rows="3"
            placeholder="Add any relevant notes about this cash tip entry..."
            value={notes}
            onChange={(e) => setNotes(e?.target?.value)}
          />
          <p className="text-xs font-caption text-muted-foreground mt-1">
            Include source, time period, or any special circumstances
          </p>
        </div>

        <div className="flex items-center gap-2 p-3 bg-warning/10 rounded-lg border border-warning/20">
          <Icon
            name="AlertTriangle"
            size={16}
            color="var(--color-warning)"
            className="flex-shrink-0"
          />
          <p className="text-xs md:text-sm font-caption text-warning">
            Manual entries are saved to the transaction log and added to the day's
            TRONC record. They can be deleted until the day is distributed.
          </p>
        </div>

        <Button
          type="submit"
          variant="default"
          size="lg"
          fullWidth
          iconName="Plus"
          iconPosition="left"
          loading={isSubmitting}
          disabled={!cashTips || !date}
        >
          Add Manual Cash Entry
        </Button>
      </form>
    </div>
  );
};

export default ManualEntryPanel;
